"use client";

import { isHeading, isLink, type Record } from "datocms-structured-text-utils";
import type { ComponentProps, FunctionComponent } from "react";
import { SRCImage } from "react-datocms";
import {
  StructuredText as DatoStructuredText,
  renderNodeRule,
  type StructuredTextGraphQlResponse,
} from "react-datocms/structured-text";

import { cn } from "@trip/utils";

import { type FragmentOf } from "~/lib/dato-graphql";
import { getHeadingAnchorId } from "~/lib/dato-utils";
import { getLinkAttributes } from "~/lib/links";

import type { imageCardFragment } from "./blocks/image-card";
import { PlaceCard, type placeCardFragment } from "./blocks/place-card";
import { TourCard, type tourCardFragment } from "./blocks/tour-card";

export type PlaceCardBlock = FragmentOf<typeof placeCardFragment>;
export type TourCardBlock = FragmentOf<typeof tourCardFragment>;
export type ImageCardBlock = FragmentOf<typeof imageCardFragment>;

type DefaultStructuredTextBlock =
  | PlaceCardBlock
  | TourCardBlock
  | ImageCardBlock;

type DefaultStructuredTextResponse = StructuredTextGraphQlResponse<
  DefaultStructuredTextBlock,
  Record
>;

// Extract block types from structured text data
type ExtractBlocks<T> =
  T extends StructuredTextGraphQlResponse<infer Blocks, any>
    ? Blocks
    : DefaultStructuredTextBlock;

// Create block components map type based on extracted blocks
type BlockComponentsMap<Blocks> = Blocks extends { __typename: string }
  ? {
      [K in Blocks["__typename"]]?: FunctionComponent<
        Extract<Blocks, { __typename: K }>
      >;
    }
  : never;

type StructuredTextProps = ComponentProps<typeof DatoStructuredText>;

export function StructuredText<
  TData extends StructuredTextGraphQlResponse<
    any,
    any
  > = DefaultStructuredTextResponse,
>({
  data,
  classNames,
  imageCaptions = false,
  blockComponents,
  customNodeRules,
  ...props
}: Omit<StructuredTextProps, "data" | "renderBlock"> & {
  classNames?: {
    ImageRecord?: string;
  };
  data: TData;
  imageCaptions?: boolean;
  blockComponents?: BlockComponentsMap<ExtractBlocks<TData>>;
}) {
  return (
    <DatoStructuredText<ExtractBlocks<TData>>
      data={data}
      customNodeRules={
        customNodeRules ?? [
          // Add HTML anchors to heading levels for in-page navigation
          renderNodeRule(isHeading, ({ node, children, key }) => {
            const HeadingTag = `h${node.level}` as const;
            return (
              <HeadingTag key={key}>
                {/* eslint-disable-next-line jsx-a11y/anchor-has-content, jsx-a11y/anchor-is-valid */}
                <a id={getHeadingAnchorId(node)} /> {children}
              </HeadingTag>
            );
          }),
          // Handle external links to open in new tab
          renderNodeRule(isLink, ({ node, children, key }) => {
            const linkAttributes = getLinkAttributes(node.url);
            return (
              <a key={key} href={node.url} {...linkAttributes}>
                {children}
              </a>
            );
          }),
        ]
      }
      renderBlock={({ record }) => {
        if (record.__typename === "TourCardRecord") {
          const Component = blockComponents?.["TourCardRecord"] ?? TourCard;
          return <Component {...record} />;
        }
        if (record.__typename === "PlaceCardRecord") {
          const Component = blockComponents?.["PlaceCardRecord"] ?? PlaceCard;
          return <Component {...record} />;
        }
        if (record.__typename === "ImageRecord") {
          const Component = blockComponents?.["ImageRecord"];
          if (Component) {
            return <Component {...record} />;
          }
          // Default ImageRecord rendering - only works with default ImageCardBlock structure
          const imageRecord = record as ImageCardBlock;
          if (imageRecord.image.responsiveImage) {
            return (
              <div className="mb-4">
                <SRCImage
                  imgClassName={cn(classNames?.ImageRecord)}
                  data={imageRecord.image.responsiveImage}
                />
                {imageCaptions && imageRecord.image.responsiveImage.title ? (
                  <div className="my-2 text-center text-sm text-neutral-500">
                    {imageRecord.image.responsiveImage.title}
                  </div>
                ) : null}
              </div>
            );
          }
          return null;
        }
        return null;
      }}
      {...props}
    />
  );
}
